import React, { useState, useEffect, useRef } from 'react'
import {
  Users,
  CheckCircle,
  RotateCcw,
  Percent
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import { toast } from 'react-toastify'
import assetAssignmentService from '../../../services/assetAssignmentService'
import assetAcquisitionService from '../../../services/assetAcquisitionService'
import { formatDate, statusBadgeClass } from '../assetUtils'
// Consolidated CSS is imported globally

const UtilizationReport = () => {
  const [data, setData] = useState([])
  const [totalAssets, setTotalAssets] = useState(0)
  const [loading, setLoading] = useState(true)
  const hasLoadedRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [assignmentsResponse, acquisitionsResponse] = await Promise.all([
        assetAssignmentService.getAllAssignments({ limit: 1000 }),
        assetAcquisitionService.getAcquisitionStats()
      ])

      if (assignmentsResponse.success) {
        setData((assignmentsResponse.data || []).map(item => ({ ...item, id: item._id })))
      } else {
        toast.error(assignmentsResponse.message || 'Failed to load utilization data')
      }

      if (acquisitionsResponse.success) {
        setTotalAssets(acquisitionsResponse.data?.totalAcquisitions || 0)
      }
    } catch (error) {
      console.error('Error loading utilization report:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load utilization report')
      }
    } finally {
      setLoading(false)
    }
  }

  const assignedCount = data.filter(item => item.status === 'Assigned').length
  const returnedCount = data.filter(item => item.status === 'Returned').length
  const utilizationRate = totalAssets > 0 ? ((assignedCount / totalAssets) * 100).toFixed(1) : '0.0'

  const columns = [
    {
      accessorKey: 'assignmentCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'assetName',
      header: 'Asset',
      cell: ({ row }) => (
        <div style={{ textAlign: 'left' }}>
          {row.original.assetName || row.original.acquisition?.assetName || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'assignedToName',
      header: 'Assigned To',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'department',
      header: 'Department',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{getValue() || '-'}</div>
      ),
    },
    {
      accessorKey: 'assignmentDate',
      header: 'Assigned On',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>{formatDate(getValue())}</div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${statusBadgeClass(getValue())}`}>{getValue() || '-'}</span>
        </div>
      ),
    },
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Assets', path: '/assets' },
          { label: 'Utilization', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Utilization Report</h1>
          <p>Asset utilization and efficiency metrics</p>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{data.length}</div>
          <div className="metric-title">Total Assignments</div>
          <div className="metric-subtitle">All records</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{assignedCount}</div>
          <div className="metric-title">Currently Assigned</div>
          <div className="metric-subtitle">In use by staff</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <RotateCcw size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Returned</span>
            </div>
          </div>
          <div className="metric-value">{returnedCount}</div>
          <div className="metric-title">Returned</div>
          <div className="metric-subtitle">Back in stock</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Percent size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Rate</span>
            </div>
          </div>
          <div className="metric-value">{utilizationRate}%</div>
          <div className="metric-title">Utilization Rate</div>
          <div className="metric-subtitle">Assigned of {totalAssets} assets</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search assignments..."
          noCard={true}
          pageSize={10}
        />
      </div>
    </div>
  )
}

export default UtilizationReport
